// A job TYPE's offers — its proposal templates — read out of a sideloaded
// graph, in the picker's order. ★ ONE reader for every surface that lists them
// (« Générer »'s per-row picker, the sales desk's « Rédiger ▾ »), so the offer
// a button defaults to and the offers a menu lists cannot disagree.
// ⚠ Position order, as the model (`JobTemplate has_many :proposal_templates
// order(:position, :id)`), the phone's quote pane and `Jobs::PresentService`
// all read it. « Générer » alone opens on the most-USED offer — its own call,
// made on top of this list (lib/generateCoverage `mostUsedOffer`).
import type { Resource } from '@/api/resources'
import { isType } from '@/api/resources'
import { num } from '@/lib/number'

// The sideloaded graph as every lib reads it: the response's `included`, or
// nothing at all.
type Included = Resource[] | undefined

export type TypeOffer = {
  id: string
  name: string
  position: number
  /** The offer's live before-tax total, or null while any of its lines lacks a
   *  price — the server's `catalog_total`, which is what a menu row may print. */
  catalogTotal: number | null
  /** « FORFAIT » — the OTHER job types this offer also sells (its typed
   *  sections). Non-empty ⇒ writing it finds or creates those jobs at the
   *  address. Empty for an ordinary offer. */
  bundledTypes: BundledType[]
}

export type BundledType = { id: string; name: string }

/** A forfait: an offer that also sells other job types. */
export const isForfait = (offer: { bundledTypes: BundledType[] }): boolean => offer.bundledTypes.length > 0

/**
 * The offer a bare « Rédiger » writes from — the FIRST ORDINARY offer, never a
 * forfait. A forfait creates other jobs at the address, so it is only ever
 * written when someone picks it by name (the server's sole-offer default
 * excludes it for the same reason). Null = no ordinary offer: the bare verb
 * writes an empty document and the forfait waits in the menu.
 */
export function defaultWriteOffer<T extends { bundledTypes: BundledType[] }>(offers: T[]): T | null {
  return offers.find(o => !isForfait(o)) ?? null
}

/** The served `bundled_types`, copied out of the resource. */
export function bundledTypesOf(offer: Resource<'proposal_template'>): BundledType[] {
  return (offer.attributes.bundled_types ?? []).map(b => ({ id: b.id, name: b.name }))
}

export function offersOfType(typeId: string | null, included: Included): TypeOffer[] {
  if (!typeId) return []
  const type = (included ?? []).find(
    (r): r is Resource<'job_template'> => isType(r, 'job_template') && r.id === typeId
  )
  const ids = new Set((type?.relationships?.proposal_templates?.data ?? []).map(d => d.id))
  return (included ?? [])
    .filter((r): r is Resource<'proposal_template'> => isType(r, 'proposal_template') && ids.has(r.id))
    .sort((a, b) => (a.attributes.position ?? 0) - (b.attributes.position ?? 0))
    .map(o => ({
      id: o.id,
      name: o.attributes.name,
      position: o.attributes.position ?? 0,
      catalogTotal: o.attributes.catalog_total == null ? null : num(o.attributes.catalog_total),
      bundledTypes: bundledTypesOf(o),
    }))
}
